import React, { useCallback, useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import RichTextEditor, { RichTextView } from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Save, Edit3, Printer } from "lucide-react";

function SectionEditor({ section, onSave, testid, ackable, onAck, acknowledged }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(section.body || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setBody(section.body || ""); }, [section.body]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(section.key, body);
      setEditing(false);
      toast.success(`Saved: ${section.title}`);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-surface-3 p-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-condensed uppercase tracking-[0.3em] text-amber">Section</div>
          <div className="font-display text-3xl uppercase tracking-wide text-white mt-1">{section.title}</div>
        </div>
        <div className="flex items-center gap-2">
          {ackable && (
            acknowledged ? (
              <span className="text-[10px] font-condensed uppercase tracking-widest text-emerald-400 border border-emerald-500/40 px-2 py-1">
                Acknowledged
              </span>
            ) : (
              <Button
                variant="outline"
                onClick={() => onAck(section.key)}
                data-testid={`ack-${section.key}`}
                className="rounded-none border-surface-3 hover:border-amber hover:text-amber h-8 text-[11px] font-condensed uppercase tracking-widest"
              >
                Sign & Acknowledge
              </Button>
            )
          )}
          {!editing ? (
            <Button
              variant="outline"
              onClick={() => setEditing(true)}
              data-testid={`edit-${section.key}`}
              className="rounded-none border-surface-3 hover:border-amber hover:text-amber h-8 text-[11px] font-condensed uppercase tracking-widest"
            >
              <Edit3 className="w-3 h-3 mr-1" /> Edit
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => { setBody(section.body); setEditing(false); }}
                className="rounded-none h-8 text-[11px] font-condensed uppercase tracking-widest"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid={`save-${section.key}`}
                className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-8 text-[11px] font-condensed uppercase tracking-widest"
              >
                <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="hairline my-4" />
      {editing ? (
        <RichTextEditor
          value={body}
          onChange={setBody}
          testid={`editor-${section.key}`}
        />
      ) : (
        <RichTextView html={section.body} />
      )}
    </div>
  );
}

export default function DocSections({ title, subtitle, endpoint, ackable = false, testid }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canPrint = user && (user.role === "head_coach" || user.role === "athletic_director");
  // Endpoint like "/staff-manual" → print kind "staff-manual"
  const printKind = (endpoint || "").replace(/^\//, "");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(data);
    } catch (e) {
      console.error("Failed to load sections", e);
      toast.error("Failed to load. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { reload(); }, [reload]);

  const save = async (key, body) => {
    const { data } = await api.put(`${endpoint}/${key}`, { body });
    setItems((prev) => prev.map(x => x.key === key ? { ...x, ...data } : x));
  };

  const ack = async (key) => {
    await api.post(`${endpoint}/${key}/acknowledge`);
    setItems((prev) => prev.map(x => x.key === key ? { ...x, acknowledged: true } : x));
    toast.success("Signed. Acknowledgement recorded.");
  };

  return (
    <div data-testid={testid}>
      <TopBar
        title={title}
        subtitle={subtitle}
        actions={
          canPrint ? (
            <Button
              onClick={() => window.open(`/documents/print/${printKind}`, "_blank", "noopener")}
              data-testid="doc-print-launch"
              title="Print this document"
              className="rounded-none bg-surface-2 hover:bg-surface-3 text-white border border-surface-3 hover:border-amber h-10 text-[11px] font-condensed uppercase tracking-widest"
            >
              <Printer className="w-3 h-3 mr-1" />
              Print
            </Button>
          ) : null
        }
      />
      <div className="p-8 space-y-4 max-w-5xl">
        {loading && <div className="text-sm text-slate-400" data-testid="doc-loading">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-sm text-slate-400">No sections yet.</div>}
        {items.map((s) => (
          <SectionEditor
            key={s.key}
            section={s}
            onSave={save}
            testid={`section-${s.key}`}
            ackable={ackable}
            onAck={ack}
            acknowledged={s.acknowledged}
          />
        ))}
      </div>
    </div>
  );
}
